import type { Step } from "react-joyride";
import { DEMO_SAVED_SEARCHES } from "../../constants/demoSavedSearches";

export type StepType = "click" | "explain";

export interface WalkthroughStep extends Step {
  stepType: StepType;
  page: string;
  /** If set, the pulsing glow is applied to this selector instead of the step target */
  glowTarget?: string;
  prepare?: () => Promise<void> | void;
}

export interface StepContext {
  navigateTo: (page: string, clientId?: string, params?: Record<string, unknown>) => void;
  janeClientId: string | null;
}

/** Wait for a DOM element to appear and be visible.
 *  Uses MutationObserver + polling fallback (for CSS transitions like Collapse). */
export function waitForElement(selector: string, timeout = 8000): Promise<Element | null> {
  const isVisible = (el: Element) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  return new Promise((resolve) => {
    const check = () => {
      const el = document.querySelector(selector);
      return el && isVisible(el) ? el : null;
    };

    const existing = check();
    if (existing) { resolve(existing); return; }

    let settled = false;
    const done = (el: Element | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(timer);
      resolve(el);
    };

    const observer = new MutationObserver(() => {
      const el = check();
      if (el) done(el);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Polling fallback: catches visibility changes during CSS transitions
    // (e.g. MUI Collapse growing height — no DOM mutations, just style changes)
    const poll = setInterval(() => {
      const el = check();
      if (el) done(el);
    }, 200);

    const timer = setTimeout(() => done(check()), timeout);
  });
}

export function createWalkthroughSteps(ctx: StepContext): WalkthroughStep[] {
  const { navigateTo, janeClientId } = ctx;
  const demoSearchCriteria = DEMO_SAVED_SEARCHES[0]?.searchCriteria ?? {
    min_price: 1500,
    max_price: 2200,
    selected_bedrooms: ["1"],
    available_only: true,
    amenities: [],
    neighborhoods: ["Midtown"],
    sort_by: "price",
  };

  return [
    // 0 — Open Jane from Needs Attention on the demo dashboard
    {
      target: '[data-tour="needs-attention-jane"]',
      content: "Start by opening Jane Demo from Needs Attention.",
      placement: "bottom" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      stepType: "click",
      page: "dashboard",
      data: { stepType: "click" },
      prepare: async () => {
        navigateTo("dashboard");
        await waitForElement('[data-tour="needs-attention-jane"]', 15000);
      },
    },

    // 1 — Move Jane to Touring
    {
      target: '[data-tour="move-to-touring-btn"]',
      content: "Move Jane into Touring so the send-list workflow becomes available.",
      placement: "bottom" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      stepType: "click",
      page: "clients",
      data: { stepType: "click" },
      prepare: async () => {
        await waitForElement('[data-tour="move-to-touring-btn"]', 8000);
      },
    },

    // 2 — Start Send List (from the client card, not the drawer)
    {
      target: '[data-tour="jane-send-list-card-btn"]',
      content: "Click Send List on Jane's card to launch her apartment search.",
      placement: "top" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      stepType: "click",
      page: "clients",
      data: { stepType: "click" },
      prepare: async () => {
        // Close the detail drawer so the client card underneath is visible,
        // then highlight Send List on the card itself (not the duplicate
        // inside the drawer).
        const closeBtn = document.querySelector(
          '[data-tour="detail-drawer-close"]'
        ) as HTMLElement | null;
        closeBtn?.click();
        // Let the MUI Drawer slide out before we measure the card button
        await new Promise((resolve) => setTimeout(resolve, 350));
        await waitForElement('[data-tour="jane-send-list-card-btn"]', 8000);
      },
    },

    // 3 — Select a floor plan
    // Target = the whole row (which owns the toggle onClick). The checkbox
    // inside has e.stopPropagation() on its onClick, so attaching the tour
    // click-advance listener to the checkbox can get swallowed during the
    // state-update cascade. Using the row's capture-phase listener fires
    // regardless of whether the user clicked the checkbox or the row body.
    {
      target: '[data-tour="floor-plan-row"]',
      glowTarget: '[data-tour="floor-plan-row"]',
      content: "Choose one floor plan to build Jane's list.",
      placement: "bottom" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      spotlightPadding: 8,
      stepType: "click",
      page: "search",
      data: { stepType: "click" },
      prepare: async () => {
        navigateTo("search", janeClientId || undefined, {
          searchCriteria: demoSearchCriteria,
        });
        await waitForElement('[data-tour="search-results"]', 12000);
        const header = document.querySelector(
          '[data-tour="property-row-header"]'
        ) as HTMLElement | null;
        header?.click();
        await waitForElement('[data-tour="floor-plan-section-ready"]', 10000);
      },
    },

    // 4 — Open the send dialog
    {
      target: '[data-tour="email-fab"]',
      content: "Open the send-list dialog for the floor plan you selected.",
      placement: "left" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      isFixed: true,
      stepType: "click",
      page: "search",
      data: { stepType: "click" },
      prepare: async () => {
        await waitForElement('[data-tour="email-fab"]', 8000);
      },
    },

    // 5 — Preview the email (final step)
    {
      target: '[data-tour="email-preview-btn"]',
      content: "Preview the email exactly the way Jane will see it.",
      placement: "top" as const,
      skipBeacon: true,
      blockTargetInteraction: false,
      stepType: "click",
      page: "search",
      data: { stepType: "click" },
      prepare: async () => {
        await waitForElement('[data-tour="email-preview-btn"]', 8000);
      },
    },
  ];
}
